import { useState } from "react";
import { toast } from "sonner";
import { Send, MessageCircle, Type, Mic } from "lucide-react";
import AudioRecorder from "@/components/AudioRecorder";

const WHATSAPP_NUMBER = "393917041816"; // +39 391 704 1816

export default function Bacheca() {
  const [mode, setMode] = useState("text"); // "text" | "audio"
  const [name, setName] = useState("");
  const [text, setText] = useState("");
  const [decade, setDecade] = useState("");

  const sendText = (e) => {
    e.preventDefault();
    if (!name.trim() || !text.trim()) {
      toast.error("Nome e messaggio sono obbligatori");
      return;
    }
    const lines = [
      "Ciao Radio Rumore Zero!",
      "",
      `Sono ${name.trim()}`,
    ];
    if (decade) lines.push(`Decade del cuore: anni ${decade.replace("s", "")}`);
    lines.push("", text.trim());
    const url = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(lines.join("\n"))}`;
    window.open(url, "_blank", "noopener,noreferrer");
    toast.success("WhatsApp aperto: invia il tuo saluto!");
    setText("");
  };

  return (
    <section
      id="bacheca"
      data-testid="bacheca-section"
      className="border-t border-[#332f2b] bg-[#0f0d0b]"
    >
      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-20 sm:py-28">
        <div className="mb-10">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#FF6B00] mb-3">
            /02 — Bacheca
          </div>
          <h2 className="font-anton uppercase text-4xl sm:text-6xl text-[#F5F2EB] leading-none">
            Saluti e <span className="text-[#FF6B00]">dediche</span>
          </h2>
          <p className="mt-4 font-mono text-sm text-[#A39A8F] max-w-xl leading-relaxed">
            Manda il tuo saluto o la tua dedica direttamente in redazione via
            WhatsApp — in testo o in audio. Lo leggeremo in diretta!
          </p>
        </div>

        <div className="grid lg:grid-cols-[1fr_1fr] gap-8 items-start">
          {/* Form */}
          <div
            data-testid="bacheca-form"
            className="p-6 rounded-2xl border border-[#332f2b] bg-[#1a1816] space-y-5"
          >
            {/* mode toggle */}
            <div className="grid grid-cols-2 p-1 rounded-xl border border-[#332f2b] bg-[#0d0b0a] font-oswald uppercase text-sm">
              <button
                type="button"
                data-testid="bacheca-mode-text"
                onClick={() => setMode("text")}
                className={`inline-flex items-center justify-center gap-2 py-2.5 rounded-lg transition ${
                  mode === "text"
                    ? "bg-[#FF6B00] text-[#12100e] shadow-[0_0_18px_rgba(255,107,0,0.35)]"
                    : "text-[#A39A8F] hover:text-[#F5F2EB]"
                }`}
              >
                <Type size={15} />
                Testo
              </button>
              <button
                type="button"
                data-testid="bacheca-mode-audio"
                onClick={() => setMode("audio")}
                className={`inline-flex items-center justify-center gap-2 py-2.5 rounded-lg transition ${
                  mode === "audio"
                    ? "bg-[#FF6B00] text-[#12100e] shadow-[0_0_18px_rgba(255,107,0,0.35)]"
                    : "text-[#A39A8F] hover:text-[#F5F2EB]"
                }`}
              >
                <Mic size={15} />
                Audio
              </button>
            </div>

            {/* shared identity fields */}
            <div>
              <label className="font-mono text-xs uppercase tracking-[0.22em] text-[#FF6B00]">
                Il tuo nome
              </label>
              <input
                data-testid="bacheca-name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                maxLength={40}
                placeholder="Es. Marco da Milano"
                className="mt-2 w-full bg-[#2a2521] border-2 border-[#4a3f33] rounded-lg px-4 py-3 text-base text-[#F5F2EB] font-mono placeholder:text-[#A39A8F] focus:outline-none focus:border-[#FF6B00] focus:ring-2 focus:ring-[#FF6B00]/30 hover:border-[#5c4f40] transition"
              />
            </div>

            <div>
              <label className="font-mono text-xs uppercase tracking-[0.22em] text-[#FF6B00]">
                Decade preferita (facoltativa)
              </label>
              <div className="mt-2 grid grid-cols-4 gap-2">
                {["60s", "70s", "80s", "90s"].map((d) => (
                  <button
                    key={d}
                    type="button"
                    data-testid={`bacheca-decade-${d}`}
                    onClick={() => setDecade(decade === d ? "" : d)}
                    className={`py-2.5 rounded-md font-oswald uppercase text-base border-2 transition ${
                      decade === d
                        ? "bg-[#FF6B00] text-[#12100e] border-[#FF6B00] shadow-[0_0_12px_rgba(255,107,0,0.4)]"
                        : "bg-[#2a2521] border-[#4a3f33] text-[#F5F2EB] hover:border-[#FF6B00] hover:text-[#FF6B00]"
                    }`}
                  >
                    {d}
                  </button>
                ))}
              </div>
            </div>

            {/* mode-specific content */}
            {mode === "text" ? (
              <form onSubmit={sendText} className="space-y-5">
                <div>
                  <label className="font-mono text-xs uppercase tracking-[0.22em] text-[#FF6B00]">
                    Il tuo saluto o dedica
                  </label>
                  <textarea
                    data-testid="bacheca-text"
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    maxLength={500}
                    rows={5}
                    placeholder="Mandate la mia canzone preferita…"
                    className="mt-2 w-full bg-[#2a2521] border-2 border-[#4a3f33] rounded-lg px-4 py-3 text-base text-[#F5F2EB] font-mono placeholder:text-[#A39A8F] focus:outline-none focus:border-[#FF6B00] focus:ring-2 focus:ring-[#FF6B00]/30 hover:border-[#5c4f40] transition resize-none leading-relaxed"
                  />
                  <div className="mt-1 text-right font-mono text-xs text-[#A39A8F]">
                    {text.length}/500
                  </div>
                </div>
                <button
                  type="submit"
                  data-testid="bacheca-submit"
                  className="w-full inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-lg bg-[#FF6B00] text-[#12100e] font-oswald uppercase text-base tracking-wider hover:bg-[#ff8226] active:scale-[0.99] transition shadow-[0_0_18px_rgba(255,107,0,0.35)]"
                >
                  <Send size={18} />
                  Manda su WhatsApp
                </button>
              </form>
            ) : (
              <AudioRecorder
                shareName={name}
                shareDecade={decade}
                onSent={() => { /* reset done inside recorder */ }}
              />
            )}
          </div>

          {/* Info card */}
          <div data-testid="bacheca-info" className="p-6 sm:p-8 rounded-2xl border border-[#FF6B00]/30 bg-[#FF6B00]/5 space-y-5">
            <div className="flex items-center gap-3">
              <span className="w-12 h-12 rounded-xl bg-[#FF6B00] text-[#12100e] flex items-center justify-center shadow-[0_0_20px_rgba(255,107,0,0.45)]">
                <MessageCircle size={22} />
              </span>
              <div>
                <div className="font-anton uppercase text-2xl text-[#F5F2EB] leading-tight">
                  Whatsapp <span className="text-[#FF6B00]">in diretta</span>
                </div>
                <div className="font-mono text-xs uppercase tracking-[0.2em] text-[#A39A8F]">
                  +39 391 704 1816
                </div>
              </div>
            </div>

            <ul className="space-y-3 font-mono text-sm text-[#A39A8F] leading-relaxed">
              <li className="flex gap-3">
                <span className="text-[#FF6B00] font-anton w-6 shrink-0">01</span>
                <span>Inserisci nome e decade preferita.</span>
              </li>
              <li className="flex gap-3">
                <span className="text-[#FF6B00] font-anton w-6 shrink-0">02</span>
                <span>Scegli se mandare un <span className="text-[#F5F2EB]">messaggio scritto</span> o <span className="text-[#F5F2EB]">un audio</span> registrato dal microfono.</span>
              </li>
              <li className="flex gap-3">
                <span className="text-[#FF6B00] font-anton w-6 shrink-0">03</span>
                <span>WhatsApp si aprirà già pronto: invia e il tuo saluto arriva in redazione.</span>
              </li>
            </ul>

            <div className="pt-4 border-t border-[#FF6B00]/20 font-mono text-[11px] uppercase tracking-[0.2em] text-[#69635c]">
              Lo leggeremo in onda · Solo musica, sempre
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
